import React, { useState, useRef, useEffect } from "react";
import { GoogleGenerativeAI } from "@google/generative-ai";
import {
  Camera,
  Image as ImageIcon,
  RotateCcw,
  Flame,
  Dumbbell,
  Cookie,
  Droplets,
  Loader2,
  CheckCircle2,
  Edit2,
  Save,
  ChevronDown,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../lib/supabaseClient";

// ⚠️ SECURITY WARNING:
// For production, move this API call to a Supabase Edge Function
const genAI = new GoogleGenerativeAI(process.env.REACT_APP_GEMINI_API_KEY);

export default function ImageFoodScanner() {
  const [filePreview, setFilePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const fileRef = useRef(null);
  const { profile, user } = useAuth();

  // --- 1. STATE MANAGEMENT ---
  
  // aiReference: Holds the raw 100g STANDARD data from Gemini (For Food DB)
  const [aiReference, setAiReference] = useState(null);
  
  // userEdit: Holds what the user actually sees & edits (For Meal Log)
  const [userEdit, setUserEdit] = useState(null);
  
  // Meal Type Selector
  const [mealType, setMealType] = useState("snack");

  // --- 2. INITIALIZATION ---
  useEffect(() => {
    // Auto-select meal type based on time of day
    const h = new Date().getHours();
    if (h >= 5 && h < 11) setMealType("breakfast");
    else if (h >= 11 && h < 15) setMealType("lunch");
    else if (h >= 15 && h < 19) setMealType("snacks");
    else setMealType("dinner");

    return () => {
      if (filePreview) URL.revokeObjectURL(filePreview);
    };
  }, [filePreview]);

  // --- 3. HELPER: IMAGE COMPRESSION ---
  const compressImage = (file) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const img = new Image();
        img.src = reader.result;
        img.onload = () => {
          const canvas = document.createElement("canvas");
          const ctx = canvas.getContext("2d");
          // Resize to max 768px to save token usage and speed up API
          const scale = 768 / Math.max(img.width, img.height);
          canvas.width = img.width * scale;
          canvas.height = img.height * scale;
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          resolve(canvas.toDataURL("image/jpeg", 0.7).split(",")[1]);
        };
      };
    });

  // --- 4. HANDLER: FILE SELECTION ---
  const pickFile = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFilePreview(URL.createObjectURL(file));
      fileRef.current = file;
      setAiReference(null);
      setUserEdit(null);
      setError("");
      setSuccessMsg("");
    }
  };

  const openCamera = () => {
    document.getElementById('camera-input').click();
  };

  // --- 5. CORE LOGIC: GEMINI ANALYSIS ---
  const uploadAndScan = async () => {
    if (!fileRef.current) return setError("Please select an image");
    
    // Check Scan Limit (Optional based on your requirements)
    const today = new Date().toISOString().split("T")[0];
    const SCAN_LIMIT = profile?.premium ? 50 : 20;
    const scansUsed = profile?.last_scan_date === today ? profile?.scans_today || 0 : 0;
    
    if (scansUsed >= SCAN_LIMIT) {
      return setError("Daily scan limit reached!");
    }

    setLoading(true);
    setError("");

    try {
      const model = genAI.getGenerativeModel({
        model: "gemini-3-flash-preview",
        generationConfig: { responseMimeType: "application/json" },
      });

      const base64 = await compressImage(fileRef.current);

      // --- PROMPT: Asks for 100g Standard AND Estimated Weight ---
      const prompt = `
        You are a nutrition expert. Analyze this food image.
        1. Identify the dish name.
        2. Estimate the TOTAL WEIGHT visible in the image (in grams).
        3. Provide nutrition values for a STANDARD 100g SERVING.
        
        Return ONLY valid JSON:
        {
          "name": "Dish Name",
          "estimated_weight_g": 100,
          "nutrition_per_100g": {
            "calories": 150,
            "protein": 10,
            "carbs": 20,
            "fat": 5,
            "fiber": 2
          }
        }
      `;

      const result = await model.generateContent([
        prompt,
        { inlineData: { data: base64, mimeType: "image/jpeg" } },
      ]);
      
      const rawText = result.response.text().replace(/```json|```/g, "").trim();
      const data = JSON.parse(rawText);

      // A. Update DB Limits
      await supabase.from("profiles").update({
        scans_today: scansUsed + 1,
        last_scan_date: today,
      }).eq("id", user.id);

      // B. Store Reference (100g Standard)
      setAiReference(data);

      // C. Calculate User View (Total Weight)
      const weight = data.estimated_weight_g;
      const p100 = data.nutrition_per_100g;

      // Formula: (ValuePer100 * TotalWeight) / 100
      setUserEdit({
        name: data.name,
        weight_g: weight,
        calories: Math.round((p100.calories * weight) / 100),
        protein: Math.round((p100.protein * weight) / 100),
        carbs: Math.round((p100.carbs * weight) / 100),
        fat: Math.round((p100.fat * weight) / 100),
      });

    } catch (err) {
      console.error(err);
      setError("Analysis failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  // --- 6. CORE LOGIC: SPLIT SAVING ---
  const saveToLog = async () => {
    if (!userEdit || !user || !aiReference) return;
    setLoading(true);

    try {
      // STEP 1: FOOD DATABASE (Handle 100g Standard)
      // We check/insert based on AI data, ignoring user edits to keep DB pure.
      
      let foodId = null;

      // A. Check if standard food exists
      let { data: existingFood } = await supabase
        .from("food_database")
        .select("id")
        .ilike("name", aiReference.name) 
        .maybeSingle();

      if (existingFood) {
        foodId = existingFood.id;
      } else {
        // B. Insert New Standard Food (100g values)
        const { data: newFood, error: insertError } = await supabase
          .from("food_database")
          .insert({
            name: aiReference.name,           // AI Name
            serving_size: "100g",             // Standard Unit
            calories: aiReference.nutrition_per_100g.calories,
            protein: aiReference.nutrition_per_100g.protein,
            carbs: aiReference.nutrition_per_100g.carbs,
            fat: aiReference.nutrition_per_100g.fat,
            fiber: aiReference.nutrition_per_100g.fiber,
          })
          .select("id")
          .single();
        
        if (insertError) throw insertError;
        foodId = newFood.id;
      }

      // STEP 2: MEAL LOGS (Handle User Edits)
      // We save the specific totals the user agreed to/edited.
      const { error: logError } = await supabase
        .from("meal_logs")
        .insert({
          user_id: user.id,
          food_id: foodId,
          meal_type: mealType,
          quantity: userEdit.weight_g,  // Actual grams eaten
          calories: userEdit.calories,  // Actual calories (could be edited by user)
          protein: userEdit.protein,
          carbs: userEdit.carbs,
          fat: userEdit.fat,
          created_at: new Date().toISOString(),
        });

      if (logError) throw logError;

      setSuccessMsg("Saved successfully!");
      
      // Reset after 2 seconds
      setTimeout(() => {
        setFilePreview(null);
        setAiReference(null);
        setUserEdit(null);
        setSuccessMsg("");
      }, 2000);

    } catch (err) {
      console.error(err);
      setError("Failed to save data.");
    } finally {
      setLoading(false);
    }
  };

  // Helper to update User Edit fields
  const updateField = (field, val) => {
    setUserEdit(prev => ({...prev, [field]: val}));
  };

  if (loading && !userEdit) return <LoadingState />;

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      <Header />

      <div className="max-w-md mx-auto p-6 space-y-6">
        
        {/* --- SECTION A: INPUTS --- */}
        {!userEdit && !loading && (
          <div className="grid grid-cols-2 gap-4">
            {/* Camera Button */}
            <button
              onClick={openCamera}
              className="py-6 bg-emerald-600 active:bg-emerald-700 text-white rounded-3xl flex flex-col items-center gap-2 shadow-lg shadow-emerald-200 transition-all"
            >
              <Camera size={28} />
              <span className="text-sm font-bold">Camera</span>
              <input 
                id="camera-input" 
                type="file" 
                accept="image/*" 
                capture="environment" 
                hidden 
                onChange={pickFile} 
              />
            </button>

            {/* Gallery Button */}
            <label className="py-6 bg-white border-2 border-dashed border-emerald-100 rounded-3xl flex flex-col items-center gap-2 cursor-pointer hover:bg-emerald-50 transition-colors">
              <input type="file" accept="image/*" hidden onChange={pickFile} />
              <ImageIcon size={28} className="text-emerald-600" />
              <span className="text-sm font-bold text-emerald-800">Gallery</span>
            </label>
          </div>
        )}

        {/* --- SECTION B: PREVIEW --- */}
        <AnimatePresence>
          {filePreview && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              className="relative rounded-3xl overflow-hidden shadow-xl"
            >
              <img
                src={filePreview}
                alt="preview"
                className="w-full h-72 object-cover"
              />

              {/* Close Button */}
              {!userEdit && !loading && (
                <button
                  onClick={() => {
                    setFilePreview(null);
                    setAiReference(null);
                    setUserEdit(null);
                  }}
                  className="absolute top-4 right-4 bg-white/90 backdrop-blur text-gray-700 p-2 rounded-full shadow-sm"
                >
                  <RotateCcw size={20} />
                </button>
              )}

              {/* Analyze Button */}
              {!userEdit && !loading && (
                <div className="absolute bottom-4 left-4 right-4">
                  <button
                    onClick={uploadAndScan}
                    className="w-full py-4 bg-black text-white rounded-2xl font-bold flex justify-center items-center gap-2 shadow-lg"
                  >
                    <Flame size={18} className="text-orange-400" />
                    Analyze Food
                  </button>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* --- ERROR MESSAGE --- */}
        {error && (
          <motion.div 
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="bg-red-50 text-red-600 p-4 rounded-xl text-center text-sm font-medium border border-red-100"
          >
            {error}
          </motion.div>
        )}

        {/* --- SUCCESS MESSAGE --- */}
        {successMsg && (
          <motion.div 
            initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }}
            className="bg-emerald-100 text-emerald-800 p-6 rounded-2xl flex flex-col items-center gap-2 text-center border border-emerald-200"
          >
            <CheckCircle2 size={40} className="text-emerald-600" />
            <span className="font-bold text-lg">Saved!</span>
            <span className="text-sm">Added to your daily diary.</span>
          </motion.div>
        )}

        {/* --- SECTION C: EDITABLE RESULTS (TOTALS) --- */}
        {userEdit && !loading && !successMsg && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="space-y-4"
          >
            {/* Header: Name & Type & Weight */}
            <div className="bg-white p-5 rounded-3xl shadow-sm border border-gray-100 space-y-4">
              
              {/* Dish Name Input */}
              <div>
                <label className="text-xs font-bold text-gray-400 uppercase ml-1">Dish Name</label>
                <div className="flex items-center gap-2 mt-1">
                  <input
                    type="text"
                    value={userEdit.name}
                    onChange={(e) => updateField("name", e.target.value)}
                    className="w-full text-lg font-black text-gray-800 bg-transparent border-b border-gray-200 focus:border-emerald-500 focus:outline-none py-1"
                  />
                  <Edit2 size={16} className="text-gray-400" />
                </div>
              </div>

              {/* Weight Input (This is Key) */}
              <div>
                <label className="text-xs font-bold text-gray-400 uppercase ml-1">Portion Size (Grams)</label>
                <input 
                    type="number"
                    value={userEdit.weight_g}
                    onChange={(e) => updateField("weight_g", Number(e.target.value))}
                    className="w-full text-lg font-black text-gray-800 bg-transparent border-b border-gray-200 focus:border-emerald-500 focus:outline-none py-1 mt-1"
                />
              </div>

              {/* Meal Type Select */}
              <div>
                <label className="text-xs font-bold text-gray-400 uppercase ml-1">Meal Type</label>
                <div className="relative mt-1">
                  <select
                    value={mealType}
                    onChange={(e) => setMealType(e.target.value)}
                    className="w-full appearance-none bg-gray-50 text-gray-700 font-bold py-3 px-4 rounded-xl focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="breakfast">Breakfast</option>
                    <option value="lunch">Lunch</option>
                    <option value="snacks">Snacks</option>
                    <option value="dinner">Dinner</option>
                  </select>
                  <ChevronDown className="absolute right-3 top-3.5 text-gray-400 pointer-events-none" size={18} />
                </div>
              </div>
            </div>

            {/* Macros Grid (Displaying Totals) */}
            <div className="grid grid-cols-2 gap-3">
              <MacroInput 
                icon={Flame} label="Calories" value={userEdit.calories} unit="kcal"
                onChange={(v) => updateField("calories", Number(v))}
              />
               <MacroInput 
                icon={Dumbbell} label="Protein" value={userEdit.protein} unit="g"
                onChange={(v) => updateField("protein", Number(v))}
              />
               <MacroInput 
                icon={Cookie} label="Carbs" value={userEdit.carbs} unit="g"
                onChange={(v) => updateField("carbs", Number(v))}
              />
               <MacroInput 
                icon={Droplets} label="Fats" value={userEdit.fat} unit="g"
                onChange={(v) => updateField("fat", Number(v))}
              />
            </div>

            {/* Action Buttons */}
            <div className="pt-2">
              <button
                onClick={saveToLog}
                className="w-full py-4 bg-emerald-600 active:bg-emerald-700 text-white rounded-2xl font-bold flex justify-center items-center gap-2 shadow-lg shadow-emerald-200 transition-all"
              >
                <Save size={20} />
                Save to Diary
              </button>
              
              <button 
                onClick={() => {
                   setAiReference(null);
                   setUserEdit(null);
                }}
                className="w-full mt-3 py-3 text-gray-400 font-bold text-xs hover:text-red-500 transition-colors"
              >
                Discard & Scan Again
              </button>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}

// --- SUB-COMPONENT: MACRO INPUT ---
function MacroInput({ icon: Icon, label, value, unit, onChange }) {
  return (
    <div className="bg-white p-4 rounded-2xl border border-gray-100 flex items-center justify-between">
      <div className="flex flex-col w-full">
        <span className="text-[10px] font-bold text-gray-400 uppercase tracking-wider flex items-center gap-1">
          <Icon size={10} /> {label}
        </span>
        <div className="flex items-baseline gap-1 mt-1">
          <input
            type="number"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="w-full font-black text-xl text-gray-800 bg-transparent focus:outline-none border-b border-transparent focus:border-emerald-200 p-0"
          />
          <span className="text-xs text-gray-400 font-bold">{unit}</span>
        </div>
      </div>
    </div>
  );
}

// --- SUB-COMPONENT: LOADING STATE ---
function LoadingState() {
  return (
    <div className="h-screen flex justify-center items-center bg-white flex-col gap-4">
      <Loader2 className="w-10 h-10 text-emerald-500 animate-spin" />
      <p className="text-sm font-bold text-gray-400 animate-pulse">Processing Food...</p>
    </div>
  );
}